import React, { useState } from 'react';
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { formatInTimeZone } from 'date-fns-tz'
import { theme, hslToHex } from '../../config/theme'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import ConfirmationModal from '../common/ConfirmationModal'
import { eventPath, eventEditPath } from '../../utils/slugs'

const formatDateInTimezone = (dateString, timezone) => {
  return formatInTimeZone(new Date(dateString), timezone, 'MMM d, yyyy')
}

const formatDateRange = (startDate, endDate, timezone) => {
  const start = formatDateInTimezone(startDate, timezone)
  const end = formatDateInTimezone(endDate, timezone)
  return start === end ? start : `${start} - ${end}`
}

const getMapImageUrl = (location) => {
  if (!location?.latitude || !location?.longitude) return null
  const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  if (!mapboxToken) return null

  const width = 800
  const height = 400
  const zoom = 14
  const markerColor = hslToHex(theme.colors.primary[500])
  const marker = `pin-s+${markerColor}(${location.longitude},${location.latitude})`

  return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${marker}/${location.longitude},${location.latitude},${zoom},0/${width}x${height}?access_token=${mapboxToken}`
}

export default function EventCard({
  event,
  user,
  distance,
  onEventDeleted,
  isLastInSection,
  lastEventElementRef
}) {
  const { isAdmin } = useAuth()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const canEdit = user && event.user && (event.user.id === user.id || isAdmin)
  const canDelete = user && event.user && (event.user.id === user.id || isAdmin)
  const isOwner = user && event.user?.id === user.id
  const isUserEvent = event.isUserEvent && !isOwner

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await api.events.delete(event.id)
      onEventDeleted(event.id)
      setShowDeleteModal(false)
    } catch (error) {
      console.error('Error deleting event:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div ref={isLastInSection ? lastEventElementRef : null}>
      <motion.div
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        whileTap={{ scale: 0.99 }}
        className="card-hover overflow-hidden"
      >
        <Link
          to={eventPath(event)}
          className="block"
        >
          {/* Image section */}
          <div className="relative h-48 bg-surface-100">
            {event.photo_url ? (
              <img
                src={event.photo_url}
                alt={event.name}
                className="w-full h-full object-cover"
              />
            ) : event.location && (
              <img
                src={getMapImageUrl(event.location)}
                alt={`Map of ${event.location.address}`}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-200" />
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-text">{event.name}</h2>
                {isOwner && (
                  <span className="badge-primary">
                    Your Event
                  </span>
                )}
                {isUserEvent && (
                  <span className="badge-primary">
                    Your Sale
                  </span>
                )}
              </div>
              {(canEdit || canDelete) && (
                <div className="flex items-center gap-1">
                  {canEdit && (
                    <Link
                      to={eventEditPath(event)}
                      onClick={(e) => e.stopPropagation()}
                      className="btn-icon min-h-[44px] min-w-[44px] p-2 rounded-full"
                      title="Edit event"
                    >
                      <PencilIcon className="h-4.5 w-4.5" />
                    </Link>
                  )}
                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setShowDeleteModal(true)
                      }}
                      className="btn-icon min-h-[44px] min-w-[44px] p-2 rounded-full
                        hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-50"
                      title="Delete event"
                    >
                      <TrashIcon className="h-4.5 w-4.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <p className="text-text-secondary mb-4 line-clamp-2">{event.description}</p>
            <div className="space-y-2">
              <div className="flex items-center text-sm text-text-muted">
                <svg
                  className="flex-shrink-0 mr-2 h-5 w-5 text-text-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {formatDateRange(event.start_date, event.end_date, event.timezone)}
              </div>
              <div className="flex items-center text-sm text-text-muted">
                <svg
                  className="flex-shrink-0 mr-2 h-5 w-5 text-text-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="truncate">
                  {event.location?.address}, {event.location?.city}, {event.location?.state} {event.location?.zip_code}
                </span>
                {distance !== null && (
                  <span className="ml-2 badge-primary whitespace-nowrap">
                    <svg className="w-3.5 h-3.5 text-primary-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 11.5a2 2 0 100-4 2 2 0 000 4z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7-7.5 11-7.5 11s-7.5-4-7.5-11a7.5 7.5 0 1115 0z" />
                    </svg>
                    {distance}
                  </span>
                )}
              </div>
              <div className="flex items-center text-sm text-text-muted">
                <svg
                  className="flex-shrink-0 mr-2 h-5 w-5 text-text-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Organized by {event.user?.name || 'Anonymous'}
              </div>
            </div>
          </div>
        </Link>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${event.name}"? This action cannot be undone and will also delete all associated sales.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete Event'}
        confirmButtonClass="bg-red-500 hover:bg-red-600"
      />
    </div>
  )
}
